// Canal « HE virtuel » : image proche d'un HES, calculée sans coloration à partir de trois scans de même grille de
// pixels (quatre avec le SHG), sur le principe des colorations virtuelles en UV profond (MUSE, microscopie UV à
// 265–280 nm) :
// - hématoxyline (noyaux) = absorbance des acides nucléiques à 265 nm, diffusion du tissu retirée par une
//   transmission de référence (325 ou 375 nm, où les acides nucléiques n'absorbent plus) : OD(265) − k × OD(réf.),
//   OD = −log10 de la transmittance (contraste [noir, verre] du canal, comme le canal sang). k = médiane du rapport des
//   OD sur le tissu : le tissu « moyen » tombe à zéro, il reste ce qui est plus riche en ADN / ARN que lui ;
// - éosine (protéines) = fluorescence excitée à 280 nm (tryptophane), ramenée à une fenêtre estimée sur l'image (du
//   niveau du fond sans tissu, seuil d'Otsu, au 99,5e centile du tissu) pour que le fond reste blanc ; à défaut, le
//   contraste de sa carte ;
// - safran (collagène, le S de HES), facultatif = signal SHG, ramené de même à une fenêtre estimée (du fond au
//   99,5e centile du signal). Colorant jaune : il absorbe surtout le bleu. Le collagène, coloré par l'éosine et le
//   safran, ressort orangé comme sur une lame HES ;
// - couleur par la loi de Beer-Lambert, comme une lame en transmission : RGB = exp(−(βH × H × gainH + βE × E × gainE
//   + βS × S × gainS)), avec les coefficients d'absorption RGB de l'hématoxyline et de l'éosine des colorations
//   virtuelles publiées (Giacomelli et al., 2016) et un jaune safran : fond blanc, noyaux violets, cytoplasme et stroma
//   roses, collagène orangé.
// Canal « Noyaux » : l'hématoxyline seule, même calcul et même couleur (deux scans : 265 nm et la référence).
import { VivLayerExtension } from '@hms-dbmi/viv';

import { otsu } from './blood';
import { pixelSize } from '../../services';

export const VHE_ID = 'vhe';  // identifiant du canal parmi les modalités
// k et échelle des noyaux à null : estimés automatiquement (estimateVirtualHe) à la première ouverture.
// c : canal du collagène (SHG par défaut s'il existe ; 'none' : sans safran).
export const VHE_DEFAULTS = {
  n: null, r: null, e: null, c: null, k: null, hScale: null, eLow: null, eHigh: null, cLow: null, cHigh: null,
  hGain: 2, eGain: 1.2, sGain: 1.5,
};
export const VHE_FALLBACK = { k: 1, hScale: 0.3 };
export const NUCLEI_ID = 'nuclei';  // canal des noyaux seuls
export const NUCLEI_DEFAULTS = { n: null, r: null, k: null, hScale: null, hGain: VHE_DEFAULTS.hGain };
const BETA_H = [0.86, 1.0, 0.3];
const BETA_E = [0.05, 1.0, 0.544];
const BETA_S = [0.03, 0.18, 1.0];  // safran : jaune orangé (transmet le rouge et le vert, absorbe le bleu)

// Module de shader avec ou sans safran (quatrième canal : le SHG), ou noyaux seuls (deux canaux) : un shader ne peut
// lire un canal absent.
const modules = {};
function moduleFor({ collagen, nuclei } = {}) {
  const key = nuclei ? 'vheModuleN' : collagen ? 'vheModuleS' : 'vheModule';
  if (modules[key]) return modules[key];
  modules[key] = {
    name: key,
    uniformTypes: {
      k: 'f32', hScale: 'f32', hGain: 'f32', eGain: 'f32', sGain: 'f32',
      betaH: 'vec3<f32>', betaE: 'vec3<f32>', betaS: 'vec3<f32>',
    },
    fs: `
uniform ${key}Uniforms {
  float k;
  float hScale;
  float hGain;
  float eGain;
  float sGain;
  vec3 betaH;
  vec3 betaE;
  vec3 betaS;
} ${key};

vec3 vhe_color(float[NUM_CHANNELS] intensity) {
  // intensity : transmittance à 265 nm (0) et de la référence (1), fluorescence à 280 nm normalisée (2), SHG
  // normalisé (3, facultatif)
  float od265 = -log(clamp(intensity[0], 0.001, 1.)) / log(10.);
  float odRef = -log(clamp(intensity[1], 0.001, 1.)) / log(10.);
  float h = max(od265 - ${key}.k * odRef, 0.) / max(${key}.hScale, 0.001);
  vec3 od = ${key}.betaH * h * ${key}.hGain;
  ${nuclei ? '' : `od += ${key}.betaE * clamp(intensity[2], 0., 1.) * ${key}.eGain;`}
  ${collagen && !nuclei ? `od += ${key}.betaS * clamp(intensity[3], 0., 1.) * ${key}.sGain;` : ''}
  return exp(-od);
}`,
    inject: { 'fs:DECKGL_MUTATE_COLOR': 'rgba = vec4(vhe_color(intensity), 1.0);' },
  };
  return modules[key];
}
const extensionOf = layer => (layer.props.extensions || []).find(e => e instanceof VirtualHeExtension);

// Réglages lus dans la prop « vhe » du calque : un changement de curseur ne met à jour que les uniformes. Options :
// collagen (quatrième canal : SHG), nuclei (noyaux seuls, deux canaux) ; en changer crée une nouvelle extension.
export class VirtualHeExtension extends VivLayerExtension {
  getVivShaderTemplates() { return { modules: [moduleFor(extensionOf(this)?.opts)] }; }

  updateState(params, extension) {
    super.updateState(params, extension);
    const v = this.props.vhe || {};
    const uniforms = {
      k: v.k ?? VHE_FALLBACK.k, hScale: v.hScale ?? VHE_FALLBACK.hScale, hGain: v.hGain ?? VHE_DEFAULTS.hGain,
      eGain: v.eGain ?? VHE_DEFAULTS.eGain, sGain: v.sGain ?? VHE_DEFAULTS.sGain, betaH: BETA_H, betaE: BETA_E, betaS: BETA_S,
    };
    const { name } = moduleFor(extension.opts);
    for (const model of this.getModels()) model.shaderInputs.setProps({ [name]: uniforms });
  }
}
VirtualHeExtension.extensionName = 'VirtualHeExtension';

// Source Viv à plusieurs canaux : canal c = image c, lue tuile par tuile dans sa propre source.
class StackSource {
  constructor(sources) {
    const { width, height } = pixelSize(sources[0]);
    this.sources = sources;
    this.shape = [1, sources.length, 1, height, width];
    this.labels = ['t', 'c', 'z', 'y', 'x'];
    this.dtype = sources[0].dtype;
    this.tileSize = sources[0].tileSize;
    this.meta = {};
  }

  getTile({ x, y, selection, signal }) {
    return this.sources[selection.c || 0].getTile({ x, y, selection: { t: 0, z: 0, c: 0 }, signal });
  }

  onTileError(err) { console.error(err); }
}

// Pyramide à plusieurs canaux, ou message expliquant pourquoi les images ne sont pas comparables pixel à pixel.
export function stackLoader(loaders) {
  const sizes = loaders.map(l => pixelSize(l[0]));
  if (sizes.some(s => s.width !== sizes[0].width || s.height !== sizes[0].height) || loaders.some(l => l.length !== loaders[0].length)) {
    return { error: 'Different dimensions: the images must be on the same pixel grid (scans rewritten on a common grid, see tools/registration/auto).' };
  }
  if (loaders.some(l => l[0].tileSize !== loaders[0][0].tileSize || l[0].dtype !== loaders[0][0].dtype)) {
    return { error: 'Different formats: prepare the display (tiles) of the images.' };
  }
  return { loader: loaders[0].map((_, k) => new StackSource(loaders.map(l => l[k]))) };
}

// Niveau réduit d'au moins minPixels pixels.
function smallLevel(loader, minPixels) {
  let level = loader.length - 1;
  while (level > 0) {
    const { width, height } = pixelSize(loader[level]);
    if (width * height >= minPixels) break;
    level -= 1;
  }
  return level;
}

// Fenêtre d'un signal de fluorescence (éosine, SHG) : du fond (médiane sous le seuil d'Otsu) au 99,5e centile du
// signal (au-dessus du seuil), dans les unités du chargeur.
function signalWindow(data) {
  const t = otsu(data);
  const below = Float64Array.from(data.filter(v => v <= t)).sort();
  const above = Float64Array.from(data.filter(v => v > t)).sort();
  const low = below.length ? below[Math.floor(below.length / 2)] : 0;
  const high = above.length ? above[Math.floor(above.length * 0.995)] : t;
  return [low, Math.max(high, low + 1)];
}

const round = (v, d) => Math.round(v * 10 ** d) / 10 ** d;
const rastersAt = (loaders, level) => Promise.all(loaders.map(l => l[Math.min(level, l.length - 1)]
  .getRaster({ selection: { t: 0, z: 0, c: 0 } })));

// Pixels où tous les rasters ont des données : au-dessus de leur noir (los), à plus de margin px d'une zone vide (bandes
// à 0 de la grille commune hors du champ d'un scan, bords adoucis par la pyramide). Sans ce masque, ces zones comptent
// comme un tissu opaque (OD 3) et gonflent l'échelle des noyaux (trans265 sous lamelle, 2026-11-03 : 3 au lieu de 0,4).
function dataMask(rasters, los, margin = 2) {
  const { width: w, height: h } = rasters[0];
  const mask = new Uint8Array(w * h);
  for (let i = 0; i < mask.length; i += 1) mask[i] = rasters.every((r, j) => r.data[i] > los[j]) ? 1 : 0;
  // érosion par un carré de 2 × margin + 1 px, en deux passes (lignes puis colonnes)
  const erode = (src, along, across, stride, step) => {
    const out = new Uint8Array(src.length);
    for (let a = 0; a < across; a += 1) {
      for (let b = 0; b < along; b += 1) {
        let v = 1;
        for (let d = -margin; d <= margin && v; d += 1) {
          const bb = b + d;
          if (bb < 0 || bb >= along || !src[a * stride + bb * step]) v = 0;
        }
        out[a * stride + b * step] = v;
      }
    }
    return out;
  };
  return erode(erode(mask, w, h, w, 1), h, w, 1, w);
}

// k et échelle des noyaux sur deux rasters (265 nm, référence) ; null si trop peu de tissu.
function nucleiCalibration(rasterN, rasterR, cal) {
  const od = ([lo, hi]) => v => -Math.log10(Math.min(1, Math.max(1e-3, (v - lo) / ((hi - lo) || 1))));
  const odN = od(cal.n);
  const odR = od(cal.r);
  const valid = dataMask([rasterN, rasterR], [cal.n[0], cal.r[0]]);
  const n = [];
  const r = [];
  for (let i = 0; i < rasterN.data.length; i += 1) {
    const vr = odR(rasterR.data[i]);
    if (valid[i] && vr > 0.02) { n.push(odN(rasterN.data[i])); r.push(vr); }
  }
  if (n.length < 100) return null;
  const ratios = Float64Array.from(n.map((v, i) => v / r[i])).sort();
  const k = Math.min(5, Math.max(0.3, ratios[Math.floor(ratios.length / 2)]));
  const nuclei = Float64Array.from(n.map((v, i) => Math.max(0, v - k * r[i]))).sort();
  const hScale = Math.max(0.05, nuclei[Math.floor(nuclei.length * 0.995)]);
  return { k: round(k, 3), hScale: round(hScale, 3) };
}

// Sur un niveau d'au moins 4 millions de pixels : k (médiane de OD(265) / OD(réf.) sur le tissu : OD de référence
// > 0,02), échelle des noyaux (99,5e centile du signal des noyaux, au moins 0,05 OD), fenêtres de l'éosine (eLow, eHigh)
// et du collagène si loaderC (cLow, cHigh), dans les unités des chargeurs ; cal = { n: [noir, verre], r: [noir, verre] }.
// null si trop peu de tissu.
export async function estimateVirtualHe(loaderN, loaderR, loaderE, cal, loaderC = null) {
  const loaders = [loaderN, loaderR, loaderE, ...(loaderC ? [loaderC] : [])];
  const [rn, rr, re, rc] = await rastersAt(loaders, smallLevel(loaderN, 4e6));
  const out = nucleiCalibration(rn, rr, cal);
  if (!out) return null;
  [out.eLow, out.eHigh] = signalWindow(re.data).map(v => round(v, 2));
  if (rc) [out.cLow, out.cHigh] = signalWindow(rc.data).map(v => round(v, 2));
  return out;
}

// Canal « Noyaux » : k et échelle seulement (même niveau, même calcul que le HE virtuel).
export async function estimateNuclei(loaderN, loaderR, cal) {
  const [rn, rr] = await rastersAt([loaderN, loaderR], smallLevel(loaderN, 4e6));
  return nucleiCalibration(rn, rr, cal);
}
